import {
    Address,
    IPayment,
    IVoucher,
    Order,
    Payment,
    Service,
    Voucher,
    IPrice,
    PriceMapper,
    ServiceRepository,
    OrderRepository,
    ServiceAllParams,
    IProShipmentApiResponse,
    IProPriceApiResponse,
} from '@packlink/packlink-sdk';

import {
    SET_CARRIERS_TERMS_AND_CONDITIONS,
    SET_CLIENT_INPUT_MODE,
    SET_COLLECTION,
    SET_COLLECTION_DATE,
    SET_COLLECTION_INTERVAL,
    SET_DROPOFF_ID,
    SET_FROM,
    SET_IN_POST_LOCKERS,
    SET_LABEL,
    SET_PACKAGES,
    SET_PRICE_DETAILS,
    SET_SERVICE,
    SET_SERVICES_TAB,
    SET_TO,
    SET_VOUCHER,
    UPDATE_SHIPMENT,
} from './action-types';

import {
    computedSelectedUpsellsState,
    getCheckoutVoucher,
    getDropoffId,
    selectedUpsellsCoverageState,
} from '@store/selectors';

import { ICollectionState, IParcel, IServiceState, IShipment, IUpsell, UpsellType } from '@types';

import { IRemoveVoucherAction } from '@store/actions/voucher';
import { setErrorAction, setLoadingAction } from '@store/actions/status';
import { clearSelectedUpsellsAction, setUpsellOptionsAction } from '@store/actions/upsell';
import OrdersRequestBodyBuilder from '@store/actions/requestBuilders/ordersRequestBodyBuilder';

import { IAppState } from '@store/reducers';
import { ClientInputMode, ICheckoutState, ServicesType } from '@store/reducers/checkout';

import moment from 'moment';
import Packlink from '@sdk';
import { calculateUpsellOptions } from '@utils/upsell';
import { ReduxAction, ReduxDispatch } from '@store';
import { requestPaymentsAction } from './payment';
import { getPaymentErrors, PaymentApiError } from '@utils';

export interface ISetVoucherAction {
    type: 'SET_VOUCHER';
    payload: IVoucher;
}

export interface ISetCarriersTermsAndConditionsAction {
    type: 'SET_CARRIERS_TERMS_AND_CONDITIONS';
    payload: boolean;
}

export interface IClientInputModeAction {
    type: 'SET_CLIENT_INPUT_MODE';
    payload: ClientInputMode;
}

export interface IUpdateShipmentAction {
    type: 'UPDATE_SHIPMENT';
    payload: IShipment;
}

export interface ISetFromAction {
    type: 'SET_FROM';
    payload: Address;
}

export interface ISetLabelAction {
    type: 'SET_LABEL';
    payload: string;
}

export interface ISetToAction {
    type: 'SET_TO';
    payload: Address;
}

export interface ISetInPostLockersAction {
    type: 'SET_IN_POST_LOCKERS';
    payload: boolean;
}

export interface ISetPriceDetailsAction {
    type: 'SET_PRICE_DETAILS';
    payload: IPrice;
}

export interface ISetServiceAction {
    type: 'SET_SERVICE';
    payload: IServiceState;
}

export interface ISetPackagesAction {
    type: 'SET_PACKAGES';
    payload?: IParcel;
}

export interface ISetCollectionAction {
    type: 'SET_COLLECTION';
    payload: ICollectionState | null;
}

export interface ISetCollectionDateAction {
    type: 'SET_COLLECTION_DATE';
    payload: moment.Moment | null;
}

export interface ISetCollectionIntervalAction {
    type: 'SET_COLLECTION_INTERVAL';
    payload: string | null;
}

export interface ISetDropOffIdAction {
    type: 'SET_DROPOFF_ID';
    payload: string | null;
}

export interface IServicesTabAction {
    type: 'SET_SERVICES_TAB';
    payload: ServicesType;
}

export type CheckoutAction =
    | IUpdateShipmentAction
    | ISetCarriersTermsAndConditionsAction
    | ISetServiceAction
    | ISetFromAction
    | ISetLabelAction
    | ISetToAction
    | ISetPriceDetailsAction
    | ISetPackagesAction
    | ISetCollectionAction
    | ISetCollectionDateAction
    | ISetCollectionIntervalAction
    | IRemoveVoucherAction
    | ISetVoucherAction
    | IClientInputModeAction
    | ISetDropOffIdAction
    | IServicesTabAction
    | ISetInPostLockersAction;

export const updateShipmentAction = (data: IShipment): IUpdateShipmentAction => ({
    type: UPDATE_SHIPMENT,
    payload: data,
});

export const setFromAction = (data: Address): ISetFromAction => ({ type: SET_FROM, payload: data });

export const setLabelAction = (url: string): ISetLabelAction => ({ type: SET_LABEL, payload: url });

export const setToAction = (data: Address): ISetToAction => ({ type: SET_TO, payload: data });

export const setPriceDetailsAction = (data: IPrice): ISetPriceDetailsAction => ({
    type: SET_PRICE_DETAILS,
    payload: data,
});

export const setServiceAction =
    (data: IServiceState): ReduxAction<void> =>
    (dispatch: ReduxDispatch): void => {
        dispatch({ type: SET_SERVICE, payload: data });
        dispatch(setUpsellOptionsAction([]));
        dispatch(clearSelectedUpsellsAction());
    };

export const setCarriersTermsAndConditionsAction = (data: boolean): ISetCarriersTermsAndConditionsAction => ({
    type: SET_CARRIERS_TERMS_AND_CONDITIONS,
    payload: data,
});

export const setInPostLockersAction = (data: boolean): ISetInPostLockersAction => ({
    type: SET_IN_POST_LOCKERS,
    payload: data,
});

export const setServicesTabAction = (data: ServicesType): IServicesTabAction => ({
    type: SET_SERVICES_TAB,
    payload: data,
});

export const resetSelectedServiceAndPriceAction =
    (): ReduxAction<void> =>
    (dispatch: ReduxDispatch): void => {
        resetServicesAndPrices(dispatch);
    };

const resetServicesAndPrices = (dispatch: ReduxDispatch): void => {
    dispatch({ type: SET_SERVICE, payload: null });
    dispatch({ type: SET_PRICE_DETAILS, payload: null });
    dispatch(setUpsellOptionsAction([]));
    dispatch(clearSelectedUpsellsAction());
};

export const resetServicesAndPricesAction =
    () =>
    (dispatch: ReduxDispatch): void => {
        dispatch({ type: SET_SERVICE, payload: null });
        dispatch({ type: SET_PRICE_DETAILS, payload: null });
        dispatch(setUpsellOptionsAction([]));
        dispatch(clearSelectedUpsellsAction());
    };

export const setPackagesAction = (data?: IParcel): ISetPackagesAction => ({ type: SET_PACKAGES, payload: data });

export const setCollectionAction = (data: ICollectionState | null): ISetCollectionAction => ({
    type: SET_COLLECTION,
    payload: data,
});

export const setCollectionDateAction = (data: moment.Moment | null): ISetCollectionDateAction => ({
    type: SET_COLLECTION_DATE,
    payload: data,
});

export const setCollectionIntervalAction = (data: string | null): ISetCollectionIntervalAction => ({
    type: SET_COLLECTION_INTERVAL,
    payload: data,
});

// TODO:: extract to SDK https://packlink.atlassian.net/browse/PLAT-665, https://packlink.atlassian.net/browse/PLAT-666
const splitShipmentAddress = (address = ''): string[] => {
    const match = address ? address.match(/.{1,40}(\s|$)/g) : null;
    if (!match || !match.length) {
        return [];
    }
    if (match.length > 2) {
        return [match.shift() as string, match.join(' ')];
    }
    return match;
};

export const getShipmentByOrderIdAction =
    (orderId: string): ReduxAction<Promise<IShipment>> =>
    async (dispatch: ReduxDispatch): Promise<IShipment> => {
        dispatch(setLoadingAction({ fetchingShipment: true }));
        return Packlink.apiClient
            .get<IProShipmentApiResponse>(`pro/shipments/by-order-id/${orderId}`)
            .then((response) => {
                const shipment = response.data.data;
                /**
                 * TODO
                 * https://packlink.atlassian.net/browse/PLAT-665
                 * https://packlink.atlassian.net/browse/PLAT-666
                 */
                const shipmentAddressFromChunks = splitShipmentAddress(shipment.from.street1 || undefined);
                const shipmentAddressToChunks = splitShipmentAddress(shipment.to.street1 || undefined);

                shipment.from.street1 = shipmentAddressFromChunks[0];
                if (shipmentAddressFromChunks[1]) {
                    shipment.from.street2 = shipmentAddressFromChunks[1];
                }
                const shipmentFrom = Address.createFromResponse(shipment.from);

                shipment.to.street1 = shipmentAddressToChunks[0];
                if (shipmentAddressToChunks[1]) {
                    shipment.to.street2 = shipmentAddressToChunks[1];
                }
                const shipmentTo = Address.createFromResponse(shipment.to);

                dispatch(updateShipmentAction(shipment as unknown as IShipment));
                dispatch(setFromAction(shipmentFrom));
                dispatch(setToAction(shipmentTo));

                // @ts-expect-error Legacy assignment
                shipment.to = shipmentTo;
                // @ts-expect-error Legacy assignment
                shipment.from = shipmentFrom;

                return shipment as unknown as IShipment;
            })
            .finally(() => {
                dispatch(setLoadingAction({ fetchingShipment: false }));
            });
    };

export const getServicesAction =
    (params: ServiceAllParams): ReduxAction<Promise<Service[] | void>> =>
    (): Promise<Service[] | void> => {
        const serviceRepository = new ServiceRepository(Packlink.apiClient);
        return serviceRepository.getServicesForSpecificSearch(params);
    };

type PriceActionResponse = { priceDetails: IPrice; upsellOptions: IUpsell[] };
export const getPriceAction =
    (source: string, secondHand: boolean): ReduxAction<Promise<PriceActionResponse | void>> =>
    async (dispatch: ReduxDispatch, getState): Promise<PriceActionResponse | void> => {
        const state: IAppState = getState();
        const { to, from, service, content, currency, packages } = state.checkout;
        const additionalInsurance = service?.insurance?.additionalInsurance;

        const selectedUpsells = computedSelectedUpsellsState(state);
        const voucherItem = state.checkout.voucher;

        const voucherName = voucherItem?.name;
        if (!service) {
            return;
        }

        const data = {
            shipments: [
                {
                    adult_signature: selectedUpsells.includes(UpsellType.ADULT_SIGNATURE),
                    content: content?.type,
                    contentvalue: content?.value,
                    content_second_hand: secondHand,
                    currency,
                    from: {
                        city: from?.city,
                        country: from?.alpha2Code,
                        state: from?.state,
                        zip_code: from?.zipCode,
                    },
                    insurance: {
                        amount: content?.value,
                        insurance_selected: selectedUpsells.includes(UpsellType.ENHANCED),
                    },
                    packages: packages ? [packages] : [],
                    proof_of_delivery: selectedUpsells.includes(UpsellType.PROOF_OF_DELIVERY),
                    service_id: parseInt(service.id, 10),
                    source,
                    to: {
                        city: to?.city,
                        country: to?.alpha2Code,
                        state: to?.state,
                        zip_code: to?.zipCode,
                    },
                    voucher_name: voucherName,
                },
            ],
        };

        dispatch(setLoadingAction({ fetchingPrice: true }));

        return Packlink.apiClient
            .post<IProPriceApiResponse>('pro/shipments/prices', data)
            .then((response) => {
                const allProducts = response.data.shipments_price_details[0].all_products;

                const priceDetails = PriceMapper.createModelFromResponse(response.data.shipments_price_details[0]);
                dispatch(setPriceDetailsAction(priceDetails));

                let upsellOptions: IUpsell[] = [];
                if (state.checkout.content && state.checkout.service) {
                    upsellOptions = calculateUpsellOptions(
                        content?.value as number,
                        service,
                        allProducts,
                        additionalInsurance,
                    );
                    dispatch(setUpsellOptionsAction(upsellOptions));
                }
                return { priceDetails, upsellOptions };
            })
            .finally((): void => {
                dispatch(setLoadingAction({ fetchingPrice: false }));
            });
    };

export const requestOrderAction = (
    payment: Payment<IPayment>,
    secondHand: boolean,
    customsInvoiceId?: string,
    carriersTermsAndConditions?: boolean,
): ReduxAction<Promise<Order | void>> => {
    return async (dispatch: ReduxDispatch, getState): Promise<Order | void> => {
        const state: IAppState = getState();
        const checkout: ICheckoutState = state.checkout;

        const to = Address.deserialize(checkout.to)?.getCreatePayload();
        const from = Address.deserialize(checkout.from)?.getCreatePayload();

        const { service, collection } = checkout;

        const selectedUpsells = computedSelectedUpsellsState(state);
        const coverage = selectedUpsellsCoverageState(state);
        const dropoffPointId = getDropoffId(state);
        const voucher = getCheckoutVoucher(state);
        const voucherName = voucher?.name;

        if (
            !from ||
            !to ||
            !checkout ||
            !checkout.service ||
            (!service?.dropOff && (!collection || !collection.date))
        ) {
            return Promise.reject(new Error('missing info to make order'));
        }

        const shipment = OrdersRequestBodyBuilder.build(
            selectedUpsells,
            checkout,
            checkout.service,
            dropoffPointId,
            from,
            coverage,
            to,
            secondHand,
        );

        dispatch(setLoadingAction({ postingOrder: true }));

        const orderRepository = new OrderRepository(Packlink.apiClient);

        return orderRepository
            .createOrder({
                orderCustomReference: 'ref',
                payment,
                shipments: [shipment],
                ...(voucherName && { voucher: Voucher.deserialize({ name: voucherName }) }),
                customsInvoiceId,
                carriersTermsAndConditions,
            })
            .then((order: Order): Order => {
                dispatch(requestPaymentsAction());
                return order;
            })
            .catch((error): void => {
                const errorMessages: PaymentApiError[] = error?.response?.data?.messages;
                const errorKeys = getPaymentErrors(errorMessages);

                dispatch(setErrorAction(errorKeys ? { ...error, paymentErrorKeys: errorKeys } : error));
                throw error;
            })
            .finally(() => dispatch(setLoadingAction({ postingOrder: false })));
    };
};

export const setVoucherAction = (payload: IVoucher): ISetVoucherAction => ({ type: SET_VOUCHER, payload });

export const setClientInputModeAction = (inputMode: ClientInputMode): IClientInputModeAction => {
    return { type: SET_CLIENT_INPUT_MODE, payload: inputMode };
};

export const setDropOffIdAction = (dropOffId: string | null): ISetDropOffIdAction => ({
    payload: dropOffId,
    type: SET_DROPOFF_ID,
});
